import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiUrlSelector, buildingUuidSelector } from 'store/config/selectors';
import { currentToken } from 'store/user/selectors';

interface UploadPhotoResponse {
  photo_uuid: string;
}

interface UseVisitorPhotoUploadReturn {
  uploadPhoto: (blob: Blob) => Promise<string | null>;
  isUploading: boolean;
  error: string | null;
}

export const useVisitorPhotoUpload = (): UseVisitorPhotoUploadReturn => {
  const apiUrl = useSelector(apiUrlSelector);
  const token = useSelector(currentToken);
  const buildingUuid = useSelector(buildingUuidSelector);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadPhoto = useCallback(
    async (blob: Blob): Promise<string | null> => {
      if (!buildingUuid || !token) {
        setError('Missing configuration');
        return null;
      }

      setIsUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append('file', blob, 'photo.jpg');

        const response = await fetch(
          `${apiUrl}/visitor-registration/v1/locations/${buildingUuid}/visitor/upload-photo`,
          {
            method: 'POST',
            headers: {
              Authorization: token,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }

        const result: UploadPhotoResponse = await response.json();
        return result.photo_uuid;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [apiUrl, token, buildingUuid],
  );

  return { uploadPhoto, isUploading, error };
};
